import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { returnCasesHttp } from '@/services/returnCases'
import type { ComputationExplanationNode, RegimeComparison } from '@/types/returnCase.types'

export interface ComputationPanelClient {
  latestRegimeComparison(caseId: string): Promise<RegimeComparison>
  compareRegimes(caseId: string, expectedRevision: number): Promise<RegimeComparison>
}

const money = (value: string) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value))

function Explanation({ node }: { node: ComputationExplanationNode }) {
  return (
    <li className="border-l border-border pl-3">
      <details>
        <summary className="cursor-pointer">
          {node.label}: {money(node.value)}
        </summary>
        <div className="mt-1 text-xs text-text-secondary">
          <div>
            {node.rule_key} · {node.rule_version}
          </div>
          <div>{node.citation}</div>
          {node.input_fact_ids.length > 0 && (
            <div>{node.input_fact_ids.length} accepted fact(s)</div>
          )}
        </div>
        {node.children.length > 0 && (
          <ul className="mt-2 space-y-2">
            {node.children.map(child => (
              <Explanation key={child.key} node={child} />
            ))}
          </ul>
        )}
      </details>
    </li>
  )
}

export default function ComputationPanel({
  caseId,
  currentRevision,
  canRun,
  client = returnCasesHttp,
}: {
  caseId: string
  currentRevision: number
  canRun: boolean
  client?: ComputationPanelClient
}) {
  const queryClient = useQueryClient()
  const comparison = useQuery({
    queryKey: ['regime-comparison', caseId],
    queryFn: () => client.latestRegimeComparison(caseId),
    enabled: Boolean(caseId),
    retry: false,
  })
  const run = useMutation({
    mutationFn: () => client.compareRegimes(caseId, currentRevision),
    onSuccess: value => queryClient.setQueryData(['regime-comparison', caseId], value),
  })
  const value = comparison.data
  const stale = Boolean(
    value &&
    (value.case_revision !== currentRevision ||
      value.old_run.status !== 'COMPLETED' ||
      value.new_run.status !== 'COMPLETED')
  )

  return (
    <section aria-label="Production tax computation" className="mt-6 border-t border-border pt-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Tax computation and regime comparison</h2>
          <p className="text-sm text-text-secondary">
            Server-derived, rule-versioned results. No totals are editable.
          </p>
        </div>
        {canRun && (
          <button className="btn btn-primary" disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Computing…' : 'Compare old and new regimes'}
          </button>
        )}
      </div>
      {run.isError && (
        <div className="alert alert-error mt-3" role="alert">
          Computation was blocked. Resolve incomplete or unreconciled inputs and retry.
        </div>
      )}
      {stale && (
        <div className="alert alert-warning mt-3" role="status">
          This comparison is stale because case inputs changed. Run a new comparison.
        </div>
      )}
      {!value && !comparison.isLoading && (
        <p className="mt-4 text-sm text-text-secondary">
          No governed comparison has been recorded.
        </p>
      )}
      {value && (
        <div className="mt-4">
          <div className="grid gap-3 md:grid-cols-2">
            {[value.old_run, value.new_run].map(item => (
              <article
                key={item.regime}
                className="rounded border border-border p-4"
                aria-label={`${item.regime === 'OLD' ? 'Old' : 'New'} regime result`}
              >
                <h3 className="font-semibold">
                  {item.regime === 'OLD' ? 'Old regime' : 'New regime'}
                </h3>
                {item.result ? (
                  <>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <dt>Total income</dt>
                      <dd>{money(item.result.total_income)}</dd>
                      <dt>Income tax</dt>
                      <dd>{money(item.result.income_tax)}</dd>
                      <dt>Rebate and relief</dt>
                      <dd>
                        {money(String(Number(item.result.rebate) + Number(item.result.relief)))}
                      </dd>
                      <dt>Surcharge / cess</dt>
                      <dd>
                        {money(
                          String(
                            Number(item.result.surcharge) -
                              Number(item.result.marginal_relief) +
                              Number(item.result.cess)
                          )
                        )}
                      </dd>
                      <dt>Total liability</dt>
                      <dd className="font-semibold">{money(item.result.total_liability)}</dd>
                      <dt>Net payable</dt>
                      <dd>{money(item.result.net_payable)}</dd>
                      <dt>Refund indication</dt>
                      <dd>{money(item.result.refund_indication)}</dd>
                    </dl>
                    <ul className="mt-4 space-y-2">
                      <Explanation node={item.result.explanation} />
                    </ul>
                  </>
                ) : (
                  <p className="mt-3 text-sm">{item.failure_codes.join(', ')}</p>
                )}
              </article>
            ))}
          </div>
          <p className="mt-3 text-sm">
            Lower computed liability: <strong>{value.lower_liability_regime}</strong> by{' '}
            {money(value.difference)}. This is a factual comparison, not a hidden recommendation.
          </p>
          <p className="mt-1 break-all font-mono text-xs text-text-secondary">
            Manifest {value.input_manifest_sha256}
          </p>
          <ul className="mt-2 list-disc pl-5 text-sm text-text-secondary">
            {value.compliance_notes.map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
